import React, { useEffect, useMemo, useState } from 'react';
import { getBenchmarks } from '../../data/kanoseiIndex';
import { useT } from '../../i18n';
import { formatPct, rebaseAllToWindow } from './timeframes';
import { formatDate } from './NavChart';

// A return line — the index account, the stock account, a bucket — against the
// S&P 500 and the NASDAQ, each as a cumulative percentage from the same start.
// Re-based to open the chosen timeframe at 0%.
//
// The index line is a TIME-WEIGHTED return, not the account's value: the
// account went from $100 to $288k on deposits, and plotting that against an
// index would plot the wires. The server infers the dated flows and checks them
// against IBKR's own figures (see benchmarks.ts); when they do not check out it
// sends no index line rather than a wrong one, and the benchmarks draw alone.
//
// Same construction as NavChart — inline SVG over a viewBox, no chart library —
// but the x axis is TIME rather than point index, because the three series
// trade on different calendars and must line up by date.

const VIEW_W = 1000;
const VIEW_H = 260;
const PAD_Y = 16;

const BENCHMARKS = { spx: '#0bcbfb', ndx: '#c9a0ff' };
const MAIN = '#8ffca0';
const colourOf = (key) => BENCHMARKS[key] ?? MAIN;
// The book's own line first, then the benchmarks.
const rank = (key) => (key === 'spx' ? 1 : key === 'ndx' ? 2 : 0);

const dayNumber = (iso) => Date.parse(`${iso}T00:00:00Z`) / 86_400_000;

/** The series' value on a date: its last point on or before it. */
const valueOn = (points, date) => {
    let found = null;
    for (const p of points) {
        if (p.date > date) break;
        found = p;
    }
    return found;
};

/**
 * `data` is a benchmarks response; without one the index's is fetched. `names`
 * labels a line the catalogue does not know (a bucket).
 */
export default function BenchmarkChart({ data: given = null, timeframe = 'all', names = {} }) {
    const { t } = useT();
    const [fetched, setFetched] = useState(null);
    const [hover, setHover] = useState(null);

    useEffect(() => {
        if (given) return undefined;
        // StrictMode double-invokes effects; a late answer from the discarded
        // mount must not land.
        let cancelled = false;
        getBenchmarks()
            .then((result) => { if (!cancelled) setFetched(result); })
            // The chart is supplementary: a failure hides it rather than
            // putting an error above the index's own figures.
            .catch(() => {});
        return () => { cancelled = true; };
    }, [given]);

    const data = given ?? fetched;

    const series = useMemo(
        () => rebaseAllToWindow(
            (data?.series ?? []).filter((s) => s.points?.length >= 2),
            timeframe,
        )
            .filter((s) => s.points.length >= 2)
            .sort((a, b) => rank(a.key) - rank(b.key)),
        [data, timeframe],
    );
    const nameOf = (key) => names[key] ?? t(`benchmarks.${key}`);

    const geometry = useMemo(() => {
        if (series.length === 0) return null;
        const dates = series.flatMap((s) => s.points.map((p) => p.date)).sort();
        const start = dayNumber(dates[0]);
        const end = dayNumber(dates.at(-1));
        const span = end - start || 1;

        // Zero is always on the chart: it is the line every series starts
        // from, and the eye reads above and below it.
        const values = [0, ...series.flatMap((s) => s.points.map((p) => p.pct))];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const range = max - min || 1;

        const x = (date) => ((dayNumber(date) - start) / span) * VIEW_W;
        const y = (pct) => PAD_Y + (1 - (pct - min) / range) * (VIEW_H - PAD_Y * 2);

        return {
            first: dates[0],
            last: dates.at(-1),
            dates: [...new Set(dates)],
            x,
            y,
            zero: y(0),
            lines: series.map((s) => ({
                key: s.key,
                points: s.points.map((p) => `${x(p.date).toFixed(2)},${y(p.pct).toFixed(2)}`).join(' '),
            })),
        };
    }, [series]);

    if (!geometry) {
        // Only once there is an answer: before it, nothing; after, a line saying so.
        return data ? <p className="navchart__empty">{t('benchmarks.unavailable')}</p> : null;
    }

    const at = hover ?? geometry.last;
    const readout = series.map((s) => ({ key: s.key, point: valueOn(s.points, at) }));

    const onMove = (event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        if (!rect.width) return;
        const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
        const start = dayNumber(geometry.first);
        const target = start + ratio * (dayNumber(geometry.last) - start);
        // Snap to the nearest date any series actually has.
        let best = geometry.dates[0];
        for (const d of geometry.dates) {
            if (Math.abs(dayNumber(d) - target) < Math.abs(dayNumber(best) - target)) best = d;
        }
        setHover(best);
    };

    return (
        <div className="navchart benchchart">
            <div className="navchart__head">
                <div className="navchart__readout">
                    <span className="navchart__title">{t('benchmarks.title')}</span>
                    <ul className="benchchart__legend">
                        {readout.map(({ key, point }) => (
                            <li key={key} className="benchchart__item" data-series={key}>
                                <span
                                    className="benchchart__swatch"
                                    style={{ background: colourOf(key) }}
                                    aria-hidden="true"
                                />
                                <span className="benchchart__name">{nameOf(key)}</span>
                                <span className="benchchart__pct">
                                    {point ? formatPct(point.pct) : '—'}
                                </span>
                            </li>
                        ))}
                    </ul>
                    {hover && <span className="navchart__date">{formatDate(hover)}</span>}
                </div>
            </div>

            <svg
                className="navchart__svg"
                viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
                preserveAspectRatio="none"
                role="img"
                aria-label={`${t('benchmarks.title')} · ${t('performance.period', {
                    from: formatDate(geometry.first), to: formatDate(geometry.last),
                })}`}
                onMouseMove={onMove}
                onMouseLeave={() => setHover(null)}
            >
                <line
                    x1="0"
                    y1={geometry.zero}
                    x2={VIEW_W}
                    y2={geometry.zero}
                    stroke="#3a3a3a"
                    strokeWidth="1"
                    strokeDasharray="4 4"
                    vectorEffect="non-scaling-stroke"
                />
                {/* Benchmarks first so the index line is drawn on top. */}
                {[...geometry.lines].reverse().map((line) => (
                    <polyline
                        key={line.key}
                        points={line.points}
                        fill="none"
                        stroke={colourOf(line.key)}
                        strokeWidth={rank(line.key) === 0 ? 2 : 1.5}
                        vectorEffect="non-scaling-stroke"
                    />
                ))}
                {hover && (
                    <>
                        <line
                            x1={geometry.x(hover)}
                            y1="0"
                            x2={geometry.x(hover)}
                            y2={VIEW_H}
                            stroke="#6f6f6f"
                            strokeOpacity="0.5"
                            strokeWidth="1"
                            vectorEffect="non-scaling-stroke"
                        />
                        {readout.filter((r) => r.point).map(({ key, point }) => (
                            <circle
                                key={key}
                                cx={geometry.x(point.date)}
                                cy={geometry.y(point.pct)}
                                r="4"
                                fill={colourOf(key)}
                            />
                        ))}
                    </>
                )}
            </svg>

            <div className="navchart__axis">
                <span>{formatDate(geometry.first)}</span>
                <span>{formatDate(geometry.last)}</span>
            </div>
        </div>
    );
}
